import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, InputAdornment, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  DesktopDateTimePicker,
  MobileDateTimePicker,
  renderTimeViewClock,
} from '@mui/x-date-pickers';
import { usePickerLayout } from '@mui/x-date-pickers/PickersLayout';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import { format } from 'date-fns';

import { useWindowSize } from '../../../../hooks/use-window-size';
import CloseActionButton from '../../ActionButtons/CloseActionButton';
import { AppBarWithButtons } from '../../app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppTabsBase, { TabConfigBase } from '../../app-tabs/AppTabsBase';
import ErrorTooltipIcon from '../ErrorTooltipIcon';
import AppTextField from '../app-text-field/AppTextField';
import { useStyles } from './AppDateTimePicker.styles';

export interface IAppDateTimePickerProps {
  value: Date | null;
  label?: string;
  onChange: (newValue: Date | null) => void;
  error?: boolean;
  helperText?: any;
  minDateTime?: Date | null;
  maxDateTime?: Date | null;
  mode?: 'light' | 'dark';
}

const AppDateTimePicker: React.FC<IAppDateTimePickerProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  minDateTime,
  maxDateTime,
  mode = 'light',
}) => {
  const [internalValue, setInternalValue] = useState<Date | null>(value);
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { mobileView } = useWindowSize();

  useEffect(() => {
    setInternalValue(value ? new Date(value) : null);
  }, [value, open]);

  const onClose = () => setOpen(false);

  const onSubmit = () => {
    onChange(internalValue);
    setOpen(false);
  };

  function renderEndAdornment() {
    const errorIcon = error && <ErrorTooltipIcon helperText={helperText} />;

    return (
      <InputAdornment
        position="end"
        style={{ right: 10, position: 'absolute' }}>
        {errorIcon}
      </InputAdornment>
    );
  }

  if (!mobileView) {
    return (
      <DesktopDateTimePicker
        enableAccessibleFieldDOMStructure={false}
        label={label}
        value={internalValue}
        onChange={(e) => onChange(e ? new Date(e) : null)}
        open={open}
        onClose={onClose}
        minDateTime={minDateTime ? new Date(minDateTime) : null}
        maxDateTime={maxDateTime ? new Date(maxDateTime) : null}
        slots={{
          textField: (params) => (
            <AppTextField
              {...params}
              mode={mode}
              onClick={() => setOpen(true)}
              inputProps={{
                ...params.inputProps,
                onClick: () => setOpen(true),
                readOnly: true,
              }}
              InputProps={{
                endAdornment: renderEndAdornment(),
              }}
            />
          ),
        }}
        slotProps={{
          popper: {
            className: classes.pickerThemeOverrides,
            placement: 'top-start',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ],
          },
        }}
      />
    );
  }

  return (
    <MobileDateTimePicker
      enableAccessibleFieldDOMStructure={false}
      label={label}
      value={internalValue}
      minDateTime={minDateTime ? new Date(minDateTime) : null}
      maxDateTime={maxDateTime ? new Date(maxDateTime) : null}
      onChange={(e) => setInternalValue(e ? new Date(e) : null)}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
      slots={{
        layout: (props) => {
          const { content } = usePickerLayout(props);
          const { view, setView } = usePickerContext();
          const dateViews: readonly string[] = ['year', 'month', 'day'];
          const currentTab = view && dateViews.includes(view) ? 'date' : 'time';
          const dateTimeTabConfig: TabConfigBase[] = [
            { label: t('common.date'), value: 'date' },
            { label: t('common.time'), value: 'time' },
          ];
          return (
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              className={classes.mobileLayout}>
              <Box
                className={classes.mobileHeader}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding={2}
                width="100%">
                <Typography variant="h2">
                  {internalValue &&
                    format(new Date(internalValue), 'dd.MM.yyyy HH:mm')}
                </Typography>
                <CloseActionButton onClick={() => setOpen(false)} />
              </Box>
              <Box paddingX={1}>
                <AppTabsBase
                  config={dateTimeTabConfig}
                  value={currentTab}
                  options={{ fullWidth: true }}
                  onChange={(newValue) =>
                    setView(newValue === 'date' ? 'day' : 'hours')
                  }
                />
              </Box>
              {content}
              <Box className={classes.actionBar}>
                <AppBarWithButtons
                  onCancelClick={() => onClose()}
                  onClick={() => onSubmit()}
                />
              </Box>
            </Box>
          );
        },
        textField: (params) => (
          <AppTextField
            {...params}
            onClick={() => setOpen(true)}
            inputProps={{
              ...params.inputProps,
              onClick: () => setOpen(true),
            }}
            InputProps={{
              endAdornment: renderEndAdornment(),
            }}
            mode={mode}
          />
        ),
      }}
      open={open}
      onClose={onClose}
      slotProps={{
        dialog: {
          fullScreen: true,
          className: classes.pickerThemeOverrides,
        },
      }}
    />
  );
};

export default AppDateTimePicker;
